export type WorkspaceId = 'brazil' | 'latam' | 'apac' | 'emea' | string

export interface Workspace {
  id: WorkspaceId
  name: string
  region: string
  subRegions: string[]
  contactCategories: string[]
}

export interface Tag {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  name: string
  color: string
  description?: string
  createdAt: string
}

export interface Form {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  name: string
  slug: string
  status: 'active' | 'draft' | 'archived'
  updated: string
  html: string
  allowedDomains?: string[]
  rateLimitPerIP?: number
  honeypotFieldName?: string
}

export interface Template {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  name: string
  subject: string
  status: 'active' | 'draft'
  html: string
}

export interface Submission {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  email: string
  form: string
  formId?: number
  mailingListIds?: number[]
  date: string
  status: 'accepted' | 'rejected'
  ip: string
  tags?: string[]
}

export interface Subscription {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  email: string
  list: string
  listId?: number
  status: 'pending' | 'confirmed' | 'unsubscribed'
  joined: string
  tags?: string[]
}

// Contact - unified person record
export interface Contact {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  email: string
  firstName?: string
  lastName?: string
  category?: string // Leader, Pastor, Influencer, etc.
  isHighProfile: boolean
  tags: string[]
  optInStatus: 'pending' | 'confirmed' | 'unsubscribed'
  optInConfirmedAt?: string
  subscribedLists: number[]
  source: 'form' | 'import' | 'manual'
  notes?: string
  createdAt: string
  updatedAt: string
}

export type Permission =
  | 'forms.view'
  | 'forms.create'
  | 'forms.edit'
  | 'forms.delete'
  | 'templates.view'
  | 'templates.create'
  | 'templates.edit'
  | 'templates.delete'
  | 'lists.view'
  | 'lists.create'
  | 'lists.edit'
  | 'lists.delete'
  | 'lists.manage_intake'
  | 'campaigns.view'
  | 'campaigns.create'
  | 'campaigns.edit'
  | 'campaigns.send'
  | 'campaigns.delete'
  | 'submissions.view'
  | 'submissions.export'
  | 'subscriptions.view'
  | 'subscriptions.manage'
  | 'subscriptions.export'
  | 'contacts.view'
  | 'contacts.create'
  | 'contacts.edit'
  | 'contacts.delete'
  | 'contacts.export'
  | 'tags.view'
  | 'tags.create'
  | 'tags.edit'
  | 'tags.delete'
  | 'configs.view'
  | 'configs.create'
  | 'configs.edit'
  | 'configs.delete'
  | 'users.view'
  | 'users.invite'
  | 'users.edit'
  | 'users.delete'
  | 'users.manage_roles'
  | 'settings.view'
  | 'settings.edit'
  | 'audit.view'
  | 'audit.export'
  | 'roles.view'
  | 'roles.create'
  | 'roles.edit'
  | 'roles.delete'

export interface Role {
  id: number
  name: string
  description: string
  permissions: Permission[]
  isSystem: boolean
  createdAt: string
}

export type UserRoleName =
  | 'Super Admin'
  | 'Workspace Admin'
  | 'Subregion Admin'
  | 'Operator'
  | 'Viewer'
  | 'Team User'
  | string

export interface User {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  name: string
  email: string
  roleId: number
  role: UserRoleName
  status: 'active' | 'invited' | 'suspended'
}

export interface FormConfiguration {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  formId: number
  templateId: number
  targetNewsletterIds: number[]
  tags: string[]
  redirectUrl?: string
  confirmationMessage?: string
  status: 'active' | 'inactive'
  createdAt: string
}

export interface Newsletter {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  name: string
  description: string
  formId?: number
  status: 'active' | 'archived'
  intakeStatus: 'open' | 'paused' | 'closed'
  intakePausedMessage?: string
  intakeClosedMessage?: string
  intakeLimit?: number
  intakePauseDate?: string
  intakeResumeDate?: string
  subscriberCount: number
  createdAt: string
}

export interface Campaign {
  id: number
  workspaceId: WorkspaceId
  subRegions: string[]
  name: string
  subject: string
  preheader: string
  fromName: string
  fromEmail: string
  htmlContent: string
  textContent?: string
  templateId?: number
  targetNewsletters: number[]
  targetTags: string[]
  targetCategories?: string[]
  status: 'draft' | 'scheduled' | 'sent' | 'exported'
  scheduledAt?: string
  sentAt?: string
  recipientCount: number
  sentCount: number
  failedCount: number
  createdAt: string
  sendBucketId?: string
  sendBucketExpiresAt?: string
  sendBucketContactCount?: number
  exportedAt?: string
}

export interface CampaignTemplate {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  name: string
  description: string
  htmlContent: string
  createdAt: string
}

export type AuditAction =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'status_changed'
  | 'duplicated'
  | 'archived'
  | 'restored'
export type AuditEntity =
  | 'form'
  | 'template'
  | 'newsletter'
  | 'campaign'
  | 'user'
  | 'tag'
  | 'form_config'
  | 'workspace'
  | 'subscription'
  | 'submission'
  | 'role'
  | 'contact'

export interface AuditEvent {
  id: number
  workspaceId: WorkspaceId
  subRegion?: string
  userId: number
  userName: string
  userEmail: string
  action: AuditAction
  entityType: AuditEntity
  entityId: number
  entityName: string
  changes?: {
    field: string
    oldValue: string | null
    newValue: string | null
  }[]
  metadata?: Record<string, any>
  timestamp: string
  ipAddress: string
  userAgent?: string
  eventType?: string
  email?: string
  newsletterName?: string
  formName?: string
}

export const permissionCategories = {
  'Signup Forms': ['forms.view', 'forms.create', 'forms.edit', 'forms.delete'],
  'Email Templates': [
    'templates.view',
    'templates.create',
    'templates.edit',
    'templates.delete',
  ],
  'Mailing Lists': [
    'lists.view',
    'lists.create',
    'lists.edit',
    'lists.delete',
    'lists.manage_intake',
  ],
  Campaigns: [
    'campaigns.view',
    'campaigns.create',
    'campaigns.edit',
    'campaigns.send',
    'campaigns.delete',
  ],
  Submissions: ['submissions.view', 'submissions.export'],
  Subscriptions: [
    'subscriptions.view',
    'subscriptions.manage',
    'subscriptions.export',
  ],
  Contacts: [
    'contacts.view',
    'contacts.create',
    'contacts.edit',
    'contacts.delete',
    'contacts.export',
  ],
  Tags: ['tags.view', 'tags.create', 'tags.edit', 'tags.delete'],
  'Form Configurations': [
    'configs.view',
    'configs.create',
    'configs.edit',
    'configs.delete',
  ],
  'Team Users': [
    'users.view',
    'users.invite',
    'users.edit',
    'users.delete',
    'users.manage_roles',
  ],
  Settings: ['settings.view', 'settings.edit'],
  'Audit Log': ['audit.view', 'audit.export'],
  'Roles & Permissions': [
    'roles.view',
    'roles.create',
    'roles.edit',
    'roles.delete',
  ],
} as const

export const allPermissions: Permission[] = Object.values(
  permissionCategories,
).flat() as Permission[]

export const initialRoles: Role[] = [
  {
    id: 1,
    name: 'Super Admin',
    description: 'Full access to all features across all workspaces',
    permissions: allPermissions,
    isSystem: true,
    createdAt: '2023-01-01',
  },
  {
    id: 2,
    name: 'Workspace Admin',
    description: 'Full access within assigned workspace',
    permissions: allPermissions.filter((p) => !p.startsWith('roles.')),
    isSystem: true,
    createdAt: '2023-01-01',
  },
  {
    id: 3,
    name: 'Subregion Admin',
    description: 'Full access within assigned sub-region',
    permissions: allPermissions.filter(
      (p) => !p.startsWith('roles.') && !p.startsWith('users.manage'),
    ),
    isSystem: true,
    createdAt: '2023-01-01',
  },
  {
    id: 4,
    name: 'Operator',
    description: 'Can manage content but not settings or users',
    permissions: [
      'forms.view',
      'forms.create',
      'forms.edit',
      'templates.view',
      'templates.create',
      'templates.edit',
      'lists.view',
      'lists.create',
      'lists.edit',
      'lists.manage_intake',
      'campaigns.view',
      'campaigns.create',
      'campaigns.edit',
      'campaigns.send',
      'submissions.view',
      'submissions.export',
      'subscriptions.view',
      'subscriptions.manage',
      'contacts.view',
      'contacts.create',
      'contacts.edit',
      'tags.view',
      'tags.create',
      'tags.edit',
      'configs.view',
      'configs.create',
      'configs.edit',
      'audit.view',
    ],
    isSystem: true,
    createdAt: '2023-01-01',
  },
  {
    id: 5,
    name: 'Viewer',
    description: 'Read-only access to all data',
    permissions: [
      'forms.view',
      'templates.view',
      'lists.view',
      'campaigns.view',
      'submissions.view',
      'subscriptions.view',
      'contacts.view',
      'tags.view',
      'configs.view',
      'users.view',
      'settings.view',
      'audit.view',
    ],
    isSystem: true,
    createdAt: '2023-01-01',
  },
  {
    id: 6,
    name: 'Team User',
    description: 'Basic access for team members',
    permissions: [
      'forms.view',
      'templates.view',
      'lists.view',
      'campaigns.view',
      'submissions.view',
      'subscriptions.view',
      'contacts.view',
      'tags.view',
    ],
    isSystem: true,
    createdAt: '2023-01-01',
  },
]

export const workspaces: Workspace[] = [
  {
    id: 'brazil',
    name: 'Brazil Team',
    region: 'LATAM',
    subRegions: ['Sao Paulo', 'Rio de Janeiro', 'Sul'],
    contactCategories: ['Leaders', 'Pastors', 'Influencers', 'Partners'],
  },
  {
    id: 'latam',
    name: 'LATAM Team',
    region: 'LATAM',
    subRegions: ['Mexico', 'Colombia', 'Argentina', 'Chile'],
    contactCategories: ['Leaders', 'Pastors', 'Influencers', 'Media'],
  },
  {
    id: 'apac',
    name: 'APAC Team',
    region: 'Asia Pacific',
    subRegions: ['India', 'Japan', 'Australia', 'Korea'],
    contactCategories: ['Leaders', 'Pastors', 'Influencers', 'Ambassadors'],
  },
  {
    id: 'emea',
    name: 'EMEA Team',
    region: 'Europe, Middle East & Africa',
    subRegions: ['UK', 'Germany', 'France', 'Dubai'],
    contactCategories: ['Leaders', 'Pastors', 'Influencers', 'VIPs'],
  },
]

export const initialTags: Tag[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    name: 'website',
    color: 'blue',
    description: 'Signups from main website',
    createdAt: '2023-01-01',
  },
  {
    id: 2,
    workspaceId: 'brazil',
    name: 'organic',
    color: 'green',
    description: 'Organic traffic signups',
    createdAt: '2023-01-01',
  },
  {
    id: 3,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    name: 'events',
    color: 'purple',
    description: 'Event registrations',
    createdAt: '2023-02-01',
  },
  {
    id: 4,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    name: 'sp',
    color: 'orange',
    description: 'Sao Paulo specific',
    createdAt: '2023-02-01',
  },
  {
    id: 5,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    name: 'mexico',
    color: 'red',
    description: 'Mexico signups',
    createdAt: '2023-03-01',
  },
]

export const initialForms: Form[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    name: 'Main Website Newsletter',
    slug: 'br-main',
    status: 'active',
    updated: '2h ago',
    html: '<form><h2>Inscreva-se</h2><input placeholder="Seu email" /></form>',
  },
  {
    id: 2,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    name: 'Sao Paulo Events',
    slug: 'sp-events',
    status: 'active',
    updated: '1d ago',
    html: '<form><h2>Eventos SP</h2><input placeholder="Email" /></form>',
  },
  {
    id: 3,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    name: 'Boletín Mexico',
    slug: 'mx-general',
    status: 'active',
    updated: '5h ago',
    html: '<form><h2>Suscríbete</h2><input placeholder="Tu correo" /></form>',
  },
  {
    id: 4,
    workspaceId: 'latam',
    subRegion: 'Colombia',
    name: 'Colombia Updates',
    slug: 'co-updates',
    status: 'active',
    updated: '3h ago',
    html: '<form><h2>Colombia</h2><input placeholder="Email" /></form>',
  },
  {
    id: 5,
    workspaceId: 'apac',
    subRegion: 'Japan',
    name: 'Japan Newsletter',
    slug: 'jp-news',
    status: 'active',
    updated: '1d ago',
    html: '<form><h2>Subscribe</h2><input placeholder="Email" /></form>',
  },
  {
    id: 6,
    workspaceId: 'brazil',
    name: 'Partner Signup',
    slug: 'br-partner',
    status: 'active',
    updated: '3d ago',
    html: '<form><h2>Partner</h2><input placeholder="Email" /></form>',
  },
]

export const initialTemplates: Template[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    name: 'Confirmação Padrão',
    subject: 'Confirme sua inscrição',
    status: 'active',
    html: '<h1>Bem-vindo!</h1>',
  },
  {
    id: 2,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    name: 'Confirmação SP',
    subject: 'Confirme - São Paulo',
    status: 'active',
    html: '<h1>Bem-vindo SP!</h1>',
  },
  {
    id: 3,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    name: 'Confirmación Mexico',
    subject: 'Confirma tu suscripción',
    status: 'active',
    html: '<h1>¡Bienvenido!</h1>',
  },
]

export const initialNewsletters: Newsletter[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    name: 'General Updates',
    description: 'Main newsletter for general updates',
    formId: 1,
    status: 'active',
    intakeStatus: 'open',
    subscriberCount: 1250,
    createdAt: '2023-01-01',
  },
  {
    id: 2,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    name: 'SP Events',
    description: 'Sao Paulo events and meetups',
    formId: 2,
    status: 'active',
    intakeStatus: 'open',
    subscriberCount: 450,
    createdAt: '2023-02-01',
  },
  {
    id: 3,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    name: 'Mexico Updates',
    description: 'News and updates for Mexico',
    formId: 3,
    status: 'active',
    intakeStatus: 'paused',
    intakePausedMessage:
      'We are currently at capacity. Please check back soon!',
    subscriberCount: 850,
    createdAt: '2023-02-15',
  },
  {
    id: 4,
    workspaceId: 'latam',
    subRegion: 'Colombia',
    name: 'Colombia News',
    description: 'Colombia community updates',
    formId: 4,
    status: 'active',
    intakeStatus: 'open',
    intakeLimit: 1000,
    subscriberCount: 620,
    createdAt: '2023-03-01',
  },
  {
    id: 5,
    workspaceId: 'brazil',
    name: 'VIP List',
    description: 'Exclusive VIP subscribers only',
    status: 'active',
    intakeStatus: 'closed',
    intakeClosedMessage:
      'This list is invite-only and not accepting new signups.',
    subscriberCount: 120,
    createdAt: '2023-04-01',
  },
]

export const initialCampaigns: Campaign[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    subRegions: [],
    name: 'October Newsletter',
    subject: 'News from The Chosen',
    preheader: 'Check out what is new',
    fromName: 'The Chosen Brazil',
    fromEmail: 'noreply@brazil.thechosen.tv',
    htmlContent: '<h1>Hello!</h1>',
    targetNewsletters: [1],
    targetTags: [],
    status: 'sent',
    sentAt: '2023-10-01T10:00:00Z',
    recipientCount: 1200,
    sentCount: 1195,
    failedCount: 5,
    createdAt: '2023-09-28',
  },
]

export const initialCampaignTemplates: CampaignTemplate[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    name: 'Monthly Newsletter Layout',
    description: 'Standard layout',
    htmlContent: '<html>...</html>',
    createdAt: '2023-01-10',
  },
]

export const initialAuditEvents: AuditEvent[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    userId: 99,
    userName: 'Super Admin',
    userEmail: 'admin@chosen.tv',
    action: 'created',
    entityType: 'newsletter',
    entityId: 1,
    entityName: 'General Updates',
    timestamp: '2023-10-24 09:00',
    ipAddress: '192.168.1.1',
  },
  {
    id: 2,
    workspaceId: 'brazil',
    userId: 99,
    userName: 'Super Admin',
    userEmail: 'admin@chosen.tv',
    action: 'updated',
    entityType: 'form',
    entityId: 1,
    entityName: 'Main Website Newsletter',
    changes: [{ field: 'status', oldValue: 'draft', newValue: 'active' }],
    timestamp: '2023-10-24 09:15',
    ipAddress: '192.168.1.1',
  },
  {
    id: 3,
    workspaceId: 'brazil',
    userId: 1,
    userName: 'Pedro Silva',
    userEmail: 'pedro@chosen.tv',
    action: 'created',
    entityType: 'subscription',
    entityId: 1,
    entityName: 'joao@example.com',
    metadata: { newsletter: 'General Updates', source: 'form_submission' },
    timestamp: '2023-10-24 10:35',
    ipAddress: '192.168.1.1',
  },
]

export const initialSubmissions: Submission[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    email: 'joao@example.com',
    form: 'Main Website Newsletter',
    formId: 1,
    mailingListIds: [1],
    date: '2023-10-24 10:30',
    status: 'accepted',
    ip: '192.168.1.1',
    tags: ['website', 'organic'],
  },
  {
    id: 2,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    email: 'maria@example.com',
    form: 'Sao Paulo Events',
    formId: 2,
    mailingListIds: [2],
    date: '2023-10-24 11:00',
    status: 'accepted',
    ip: '192.168.1.2',
    tags: ['events', 'sp'],
  },
  {
    id: 3,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    email: 'carlos@example.com',
    form: 'Boletín Mexico',
    formId: 3,
    mailingListIds: [3],
    date: '2023-10-24 09:00',
    status: 'accepted',
    ip: '192.168.1.3',
    tags: ['mexico'],
  },
  {
    id: 4,
    workspaceId: 'brazil',
    email: 'ana@example.com',
    form: 'Main Website Newsletter',
    formId: 1,
    mailingListIds: [1],
    date: '2023-10-25 14:20',
    status: 'accepted',
    ip: '192.168.1.4',
    tags: ['website'],
  },
  {
    id: 5,
    workspaceId: 'brazil',
    email: 'pedro@example.com',
    form: 'Main Website Newsletter',
    formId: 1,
    mailingListIds: [1],
    date: '2023-10-25 16:45',
    status: 'accepted',
    ip: '192.168.1.5',
    tags: ['website', 'organic'],
  },
  {
    id: 6,
    workspaceId: 'brazil',
    email: 'lucia@example.com',
    form: 'Main Website Newsletter',
    formId: 1,
    mailingListIds: [1, 5],
    date: '2023-10-26 08:15',
    status: 'accepted',
    ip: '192.168.1.6',
    tags: ['website'],
  },
]

export const initialSubscriptions: Subscription[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    email: 'joao@example.com',
    list: 'General Updates',
    listId: 1,
    status: 'confirmed',
    joined: '2023-10-24',
    tags: ['website', 'organic'],
  },
  {
    id: 2,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    email: 'maria@example.com',
    list: 'SP Events',
    listId: 2,
    status: 'confirmed',
    joined: '2023-10-24',
    tags: ['events', 'sp'],
  },
  {
    id: 3,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    email: 'carlos@example.com',
    list: 'Mexico Updates',
    listId: 3,
    status: 'pending',
    joined: '2023-10-24',
    tags: ['mexico'],
  },
  {
    id: 4,
    workspaceId: 'brazil',
    email: 'ana@example.com',
    list: 'General Updates',
    listId: 1,
    status: 'confirmed',
    joined: '2023-10-25',
    tags: ['website'],
  },
  {
    id: 5,
    workspaceId: 'brazil',
    email: 'pedro@example.com',
    list: 'General Updates',
    listId: 1,
    status: 'confirmed',
    joined: '2023-10-25',
    tags: ['website', 'organic'],
  },
  {
    id: 6,
    workspaceId: 'brazil',
    email: 'lucia@example.com',
    list: 'General Updates',
    listId: 1,
    status: 'confirmed',
    joined: '2023-10-26',
    tags: ['website'],
  },
  {
    id: 7,
    workspaceId: 'brazil',
    email: 'lucia@example.com',
    list: 'VIP List',
    listId: 5,
    status: 'confirmed',
    joined: '2023-10-26',
    tags: ['vip'],
  },
]

// Contacts - unified person records with opt-in status
export const initialContacts: Contact[] = [
  // Confirmed contacts from Brazil
  {
    id: 1,
    workspaceId: 'brazil',
    email: 'joao@example.com',
    firstName: 'João',
    lastName: 'Silva',
    category: undefined,
    isHighProfile: false,
    tags: ['website', 'organic'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-10-24',
    subscribedLists: [1],
    source: 'form',
    createdAt: '2023-10-24',
    updatedAt: '2023-10-24',
  },
  {
    id: 2,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    email: 'maria@example.com',
    firstName: 'Maria',
    lastName: 'Santos',
    category: undefined,
    isHighProfile: false,
    tags: ['events', 'sp'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-10-24',
    subscribedLists: [2],
    source: 'form',
    createdAt: '2023-10-24',
    updatedAt: '2023-10-24',
  },
  {
    id: 3,
    workspaceId: 'brazil',
    email: 'ana@example.com',
    firstName: 'Ana',
    lastName: 'Costa',
    category: undefined,
    isHighProfile: false,
    tags: ['website'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-10-25',
    subscribedLists: [1],
    source: 'form',
    createdAt: '2023-10-25',
    updatedAt: '2023-10-25',
  },
  {
    id: 4,
    workspaceId: 'brazil',
    email: 'pedro@example.com',
    firstName: 'Pedro',
    lastName: 'Oliveira',
    category: undefined,
    isHighProfile: false,
    tags: ['website', 'organic'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-10-25',
    subscribedLists: [1],
    source: 'form',
    createdAt: '2023-10-25',
    updatedAt: '2023-10-25',
  },
  {
    id: 5,
    workspaceId: 'brazil',
    email: 'lucia@example.com',
    firstName: 'Lucia',
    lastName: 'Ferreira',
    category: undefined,
    isHighProfile: false,
    tags: ['website', 'vip'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-10-26',
    subscribedLists: [1, 5],
    source: 'form',
    createdAt: '2023-10-26',
    updatedAt: '2023-10-26',
  },

  // High-profile contacts (Leaders, Pastors, Influencers)
  {
    id: 6,
    workspaceId: 'brazil',
    email: 'pastor.marcos@igreja.com',
    firstName: 'Marcos',
    lastName: 'Almeida',
    category: 'Pastors',
    isHighProfile: true,
    tags: ['vip'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-09-15',
    subscribedLists: [1, 5],
    source: 'manual',
    notes: 'Senior Pastor - Igreja Batista Central',
    createdAt: '2023-09-15',
    updatedAt: '2023-10-01',
  },
  {
    id: 7,
    workspaceId: 'brazil',
    subRegion: 'Sao Paulo',
    email: 'influencer.julia@gmail.com',
    firstName: 'Julia',
    lastName: 'Mendes',
    category: 'Influencers',
    isHighProfile: true,
    tags: ['sp', 'vip'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-08-20',
    subscribedLists: [1, 2],
    source: 'manual',
    notes: '500K followers on Instagram',
    createdAt: '2023-08-20',
    updatedAt: '2023-10-15',
  },
  {
    id: 8,
    workspaceId: 'brazil',
    email: 'leader.roberto@thechosen.tv',
    firstName: 'Roberto',
    lastName: 'Nascimento',
    category: 'Leaders',
    isHighProfile: true,
    tags: ['vip'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-07-01',
    subscribedLists: [1, 5],
    source: 'manual',
    notes: 'Regional Director - South Brazil',
    createdAt: '2023-07-01',
    updatedAt: '2023-09-20',
  },
  {
    id: 9,
    workspaceId: 'brazil',
    email: 'partner.empresa@corp.com',
    firstName: 'Carlos',
    lastName: 'Empresário',
    category: 'Partners',
    isHighProfile: true,
    tags: ['vip'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-06-15',
    subscribedLists: [5],
    source: 'manual',
    notes: 'Corporate sponsor',
    createdAt: '2023-06-15',
    updatedAt: '2023-08-10',
  },

  // Pending contact (not yet confirmed double opt-in)
  {
    id: 10,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    email: 'carlos@example.com',
    firstName: 'Carlos',
    lastName: 'Garcia',
    category: undefined,
    isHighProfile: false,
    tags: ['mexico'],
    optInStatus: 'pending',
    subscribedLists: [3],
    source: 'form',
    createdAt: '2023-10-24',
    updatedAt: '2023-10-24',
  },

  // More high-profile contacts in LATAM
  {
    id: 11,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    email: 'pastor.miguel@iglesia.mx',
    firstName: 'Miguel',
    lastName: 'Hernandez',
    category: 'Pastors',
    isHighProfile: true,
    tags: ['mexico', 'vip'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-09-01',
    subscribedLists: [3],
    source: 'manual',
    notes: 'Lead Pastor - Mexico City',
    createdAt: '2023-09-01',
    updatedAt: '2023-10-01',
  },
  {
    id: 12,
    workspaceId: 'latam',
    subRegion: 'Colombia',
    email: 'influencer.sofia@gmail.com',
    firstName: 'Sofia',
    lastName: 'Rodriguez',
    category: 'Influencers',
    isHighProfile: true,
    tags: ['vip'],
    optInStatus: 'confirmed',
    optInConfirmedAt: '2023-08-15',
    subscribedLists: [4],
    source: 'manual',
    notes: 'Christian blogger - 200K followers',
    createdAt: '2023-08-15',
    updatedAt: '2023-09-20',
  },
]

export const initialUsers: User[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    name: 'Pedro Silva',
    email: 'pedro@chosen.tv',
    roleId: 2,
    role: 'Workspace Admin',
    status: 'active',
  },
  {
    id: 2,
    workspaceId: 'latam',
    subRegion: 'Mexico',
    name: 'Sofia Rodriguez',
    email: 'sofia@chosen.tv',
    roleId: 3,
    role: 'Subregion Admin',
    status: 'active',
  },
  {
    id: 99,
    workspaceId: 'brazil',
    name: 'Super Admin',
    email: 'admin@chosen.tv',
    roleId: 1,
    role: 'Super Admin',
    status: 'active',
  },
]

export const initialConfigs: FormConfiguration[] = [
  {
    id: 1,
    workspaceId: 'brazil',
    formId: 1,
    templateId: 1,
    targetNewsletterIds: [1],
    tags: ['website', 'organic'],
    redirectUrl: 'https://thechosen.tv/thank-you',
    status: 'active',
    createdAt: '2023-10-20',
  },
]
